import { useMemo, useState } from 'react';
import { Info, Mail, MapPin, Phone, Sparkles, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';

import { INSIGHT_ORDER, INSIGHT_TYPES } from '../campaigns-config';
import {
  freshnessFromAge,
  customerAccounts,
  customerAddress,
  customerBalanceTrend,
  customerDoNotContact,
  customerEmail,
  customerHolderAge,
  customerLastActivity,
  customerOfficer,
  customerPhone,
  formatOpenedDate,
  formatRelativeDays,
  signalReason,
  tenureLabel,
} from '../customer-derived';
import { rankEligibleProducts, type RankedProduct } from '../customer-eligibility';
import { useProductPriority } from '@/features/settings/useSettings';
import type { InsightType } from '@/features/intelligence-dashboard/insight.types';
import type { FactItem, RationaleResult } from '../insights/useRationale';
import { useRationales, type RationaleBatchItem } from '../insights/useRationales';
import { useInsightExplanation, type ExplanationRequest } from './useInsightExplanation';
import { ReferPopover } from './ReferPopover';

interface CustomerDrawerProps {
  customer: Customer;
  products: Product[];
  onClose: () => void;
  onStartCampaign: (id: string) => void;
  /** SCRUM-389: teller mode strips the campaign CTA — refer is the only action. */
  tellerMode?: boolean;
}

const SECTION_HEADING =
  'text-text-3 font-mono text-[0.625rem] font-semibold tracking-[0.08em] uppercase';

/**
 * The 420px customer detail drawer (SCRUM-207) — a faithful port of Ryan's
 * reasoning inspector: six stacked sections (Contact, Relationship, Signals,
 * Portfolio, Recos, Activity) in a single scroll body. The anchor-chip
 * quick-scroll row was removed per Ryan's v7 feedback (SCRUM-478 — no
 * prototype counterpart). Footer actions are Start campaign (primary) and
 * Refer (popover).
 */
export function CustomerDrawer({
  customer: c,
  products,
  onClose,
  onStartCampaign,
  tellerMode = false,
}: CustomerDrawerProps) {
  const [openSignal, setOpenSignal] = useState<InsightType | null>(null);

  const holderAge = customerHolderAge(c);
  const isBusiness = c.accountType === 'Business';
  const dnc = customerDoNotContact(c);
  const officer = customerOfficer(c);
  const portfolio = customerAccounts(c);
  const lastTxn = customerLastActivity(c);
  const trend = customerBalanceTrend(c);
  const activeInsights = INSIGHT_ORDER.filter((k) => c.insights[k]?.active);

  // Memoized so rationaleItems has a stable identity and the batch hook's idsKey
  // does not churn across renders (SCRUM-266).
  // SCRUM-46: priority orders the eligible set FIRST, then truncate to top-3
  // (RT-C decision) — a high-priority product can enter the top-3 over a
  // higher-score lower-priority one. `order` is in the deps (RT-B) so a
  // Settings save re-sorts live.
  const { order: priorityOrder } = useProductPriority();
  const recos = useMemo(
    () => rankEligibleProducts(c, products, priorityOrder).slice(0, 3),
    [c, products, priorityOrder],
  );
  const facts = useMemo(() => deriveCustomerFacts(c), [c]);

  // SCRUM-266: one batch request for all reco rationales (replaces N per-card calls).
  const rationaleItems: RationaleBatchItem[] = useMemo(
    () =>
      recos.map((r) => ({
        id: r.product.id,
        productName: r.product.name,
        icpMatchCount: r.eligibility.met,
        facts,
      })),
    [recos, facts],
  );
  const { data: rationales, isLoading: rationalesLoading } = useRationales(rationaleItems);

  // min-h-[640px]: Ryan's .reasoning-inspector floor. Safe again under the
  // SCRUM-353 layout — the PAGE scrolls (v6 model), so the drawer may exceed a
  // short ledger without being clipped by a fixed-height shell. (The SCRUM-284
  // removal applied to the old viewport-bounded flex wrapper.)
  return (
    <aside className="border-border-0 bg-bg-4 shadow-tk-sm rounded-tk-lg flex h-full min-h-[640px] w-[420px] flex-shrink-0 flex-col overflow-hidden border">
      {/* Sticky header */}
      <div className="border-border-1 flex items-start justify-between border-b px-4 py-3">
        <div>
          <div className="text-text-0 font-display text-[0.9375rem] font-semibold">{c.name}</div>
          <div className="mt-1 flex items-center gap-2">
            {/* v7 .rip-pill (SCRUM-369) */}
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-mono text-[0.625rem] font-semibold tracking-[0.04em] uppercase ${
                isBusiness ? 'bg-brand-dim text-brand-primary' : 'bg-bg-3 text-text-1'
              }`}
            >
              {c.accountType}
            </span>
            {holderAge != null && (
              <span className="text-text-2 text-[0.6875rem] font-medium">Age {holderAge}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-text-3 font-mono text-[0.625rem] tracking-[0.04em]">
            {c.portfolioId.toUpperCase()}
          </span>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            className="text-text-3 hover:text-text-0"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Scrolling sections — only the body scrolls (the card has overflow-hidden).
          .scrollbar-thin gives a subtle themed scrollbar (thumb=--border-1) and a
          stable gutter so content doesn't shift when the scrollbar toggles
          (replaces the heavy OS-default scrollbar; Ryan bankIQ 4). SCRUM-284. */}
      <div className="flex-1 scrollbar-thin space-y-5 overflow-y-auto px-4 py-4">
        {/* Contact */}
        <section className="space-y-2">
          <h3 className={SECTION_HEADING}>Contact</h3>
          <ContactRow
            icon={<Phone className="h-3.5 w-3.5" />}
            value={customerPhone(c)}
            blocked={dnc.call}
            tag="DNC"
          />
          <ContactRow
            icon={<Mail className="h-3.5 w-3.5" />}
            value={customerEmail(c)}
            blocked={dnc.email}
            tag="DNE"
          />
          <ContactRow
            icon={<MapPin className="h-3.5 w-3.5" />}
            value={customerAddress(c)}
            blocked={dnc.mail}
            tag="DNM"
          />
        </section>

        {/* Relationship */}
        <section className="space-y-2">
          <h3 className={SECTION_HEADING}>Relationship</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <RelCell label="Branch" value={c.branch || '—'} sub={c.region ? `· ${c.region}` : ''} />
            {/* Officer role as a v7 boxed tag, not "· role" text (SCRUM-370). */}
            <RelCell label="Officer" value={officer.name} tag={officer.role} />
            <RelCell
              label="Customer since"
              value={formatOpenedDate(c.ageMo)}
              sub={`· ${tenureLabel(c.ageMo)}`}
            />
            <RelCell label="Total balance" value={`$${c.balance.toLocaleString('en-US')}`} />
          </div>
        </section>

        {/* Active signals — AI explanation via SCRUM-221 */}
        <section className="space-y-2">
          <h3 className={SECTION_HEADING}>Active signals</h3>
          {activeInsights.length === 0 ? (
            <p className="text-text-3 text-sm">No active intelligence triggers.</p>
          ) : (
            <div>
              {activeInsights.map((k) => {
                const flag = c.insights[k];
                if (!flag) return null;
                return (
                  <SignalCard
                    key={k}
                    insightType={k}
                    flag={flag}
                    customer={c}
                    openSignal={openSignal}
                    onToggle={() => {
                      setOpenSignal(openSignal === k ? null : k);
                    }}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* Portfolio */}
        <section className="space-y-2">
          <h3 className={`${SECTION_HEADING} flex items-center gap-2`}>
            Portfolio
            {/* v7 .rip-section__pill (SCRUM-371) */}
            <span className="bg-brand-dim text-brand-primary inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[0.5625rem] font-semibold normal-case">
              {portfolio.concentration}
            </span>
          </h3>
          {portfolio.accounts.length === 0 ? (
            <p className="text-text-3 text-sm">No products on record.</p>
          ) : (
            <div className="flex flex-col">
              {portfolio.accounts.map((a) => (
                <div
                  key={`${a.code}-${a.acctNum}`}
                  className="border-border-0 flex items-center gap-2.5 border-b py-1.5 text-[0.8125rem] last:border-0"
                >
                  {/* v7 .rip-portfolio__code — neutral bg-3 bordered 32px box, not gold. */}
                  <span className="bg-bg-3 border-border-0 text-text-2 inline-flex w-8 flex-shrink-0 items-center justify-center rounded-sm border py-0.5 font-mono text-[0.625rem] font-semibold">
                    {a.code}
                  </span>
                  <span className="text-text-1 min-w-0 flex-1 text-[0.8125rem] font-medium">
                    {a.label}
                    <span className="text-text-3 block font-mono text-[0.625rem]">
                      {a.acctNum} · Opened {a.opened}
                    </span>
                  </span>
                  <span className="text-text-0 font-mono text-xs font-semibold tabular-nums">
                    ${a.balance.toLocaleString('en-US')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recos — AI-generated rationale via SCRUM-51 */}
        <section className="space-y-2">
          <h3 className={SECTION_HEADING}>Recommended products</h3>
          {recos.length === 0 ? (
            <p className="text-text-3 text-sm">
              No products this customer is currently eligible for.
            </p>
          ) : (
            recos.map((r) => (
              <RecoCard
                key={r.product.id}
                reco={r}
                rationale={rationales?.[r.product.id]}
                loading={rationalesLoading}
              />
            ))
          )}
        </section>

        {/* Activity */}
        <section className="space-y-2">
          <h3 className={SECTION_HEADING}>Activity</h3>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-2">Last transaction</span>
              <span className="text-text-1">{formatRelativeDays(lastTxn)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-2">Balance trend (30d)</span>
              <span
                style={{
                  color:
                    trend.direction === 'up'
                      ? 'var(--success)'
                      : trend.direction === 'down'
                        ? 'var(--danger)'
                        : 'var(--text-3)',
                }}
              >
                {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'}{' '}
                {trend.pct > 0 ? '+' : ''}
                {trend.pct}%
              </span>
            </div>
          </div>
        </section>
      </div>

      {/* Footer actions — teller mode keeps refer only (v7 SCRUM-389). */}
      <div className="border-border-1 flex items-center gap-2 border-t px-4 py-3">
        {!tellerMode && (
          <Button
            className="flex-1"
            onClick={() => {
              onStartCampaign(c.id);
            }}
          >
            Start campaign
          </Button>
        )}
        <ReferPopover customer={c} products={products} />
      </div>
    </aside>
  );
}

function ContactRow({
  icon,
  value,
  blocked,
  tag,
}: {
  icon: React.ReactNode;
  value: string;
  blocked: boolean;
  tag: string;
}) {
  return (
    <div className="flex items-center gap-2.5 text-sm">
      <span className="text-text-3">{icon}</span>
      <span className={`flex-1 ${blocked ? 'text-text-3 line-through' : 'text-text-1'}`}>
        {value}
      </span>
      {blocked && (
        <span className="text-danger bg-danger/10 rounded px-1 py-0.5 font-mono text-[0.5625rem] font-semibold">
          {tag}
        </span>
      )}
    </div>
  );
}

function RelCell({
  label,
  value,
  sub,
  tag,
}: {
  label: string;
  value: string;
  sub?: string;
  /** v7 .rip-relcell__tag — a boxed label beside the value (e.g. the officer role). */
  tag?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-text-3 font-mono text-[0.5625rem] font-semibold tracking-[0.06em] uppercase">
        {label}
      </span>
      <span className="text-text-0 flex flex-wrap items-center gap-1.5 text-[0.8125rem] font-medium">
        {value}
        {tag && (
          <span
            title={tag}
            className="border-border-0 bg-bg-3 text-text-2 inline-flex items-center rounded-sm border px-1.5 py-0.5 font-mono text-[0.5625rem] font-semibold"
          >
            {tag}
          </span>
        )}
        {sub && <span className="text-text-3 font-normal"> {sub}</span>}
      </span>
    </div>
  );
}

// ── RecoCard ──────────────────────────────────────────────────────────────────
// Presentational (SCRUM-266): the rationale now comes from the drawer-level batch
// hook (useRationales) as a prop, so this no longer calls a hook and the
// Rules-of-Hooks reason it was originally extracted no longer applies.

/**
 * Derives non-PII customer facts suitable for the rationale API request.
 * Only tier, relationship age, and active insight signal are included —
 * no account numbers, balances, or personal identifiers.
 */
function deriveCustomerFacts(customer: Customer): FactItem[] {
  const facts: FactItem[] = [
    { label: 'Account tier', value: customer.tier },
    { label: 'Relationship age', value: `${customer.ageMo.toString()} months` },
  ];
  // Include the first active intelligence signal if present (non-PII).
  const activeSignal = Object.entries(customer.insights).find(([, v]) => v?.active);
  if (activeSignal) {
    facts.push({ label: 'Active signal', value: activeSignal[0] });
  }
  return facts;
}

interface RecoCardProps {
  reco: RankedProduct;
  /** Resolved rationale for this product from the batch hook (keyed by product id). */
  rationale: RationaleResult | undefined;
  loading: boolean;
}

/**
 * A single recommended-product card in the customer drawer Recos section.
 * Shows the AI-generated rationale (SCRUM-51) with an "AI-generated" badge when
 * available. Falls back silently (no rationale text, no badge) when the rationale
 * is missing or aiGenerated:false. Rationale is supplied by the drawer's batch
 * hook (SCRUM-266) — this component does not fetch.
 */
function RecoCard({ reco: r, rationale, loading }: RecoCardProps) {
  const rationaleText = rationale?.content ?? '';
  const isAiGenerated = rationale?.aiGenerated ?? false;

  return (
    <div className="border-border-1 rounded-md border p-2.5">
      {/* Header: product name + ICP score */}
      <div className="flex items-center justify-between">
        <span className="text-text-0 text-sm font-medium">{r.product.name}</span>
        <span className="text-text-2 font-mono text-xs">
          {r.eligibility.met}/{r.eligibility.total} · {r.score}%
        </span>
      </div>

      {/* AI-generated rationale (SCRUM-51) */}
      <div className="mt-1 mb-1.5 min-h-[1.25rem]">
        {loading ? (
          <div
            className="bg-bg-3 mt-1 h-4 w-3/4 animate-pulse rounded"
            aria-label="Loading rationale"
          />
        ) : rationaleText ? (
          <>
            <p className="text-text-2 text-xs leading-relaxed">{rationaleText}</p>
            {isAiGenerated && (
              <span className="text-text-3 mt-0.5 flex items-center gap-1 text-[0.625rem]">
                <Sparkles className="h-2.5 w-2.5" aria-hidden="true" />
                AI-generated
              </span>
            )}
          </>
        ) : null}
      </div>

      {/* Eligibility rules */}
      <div className="mt-1 space-y-1">
        {r.eligibility.rules.map((rule, i) => (
          <div
            key={`${r.product.id}-${String(i)}`}
            className={`flex items-center gap-1.5 text-xs ${
              rule.met ? 'text-text-1' : 'text-text-3'
            }`}
          >
            <span style={{ color: rule.met ? 'var(--success)' : 'var(--text-3)' }}>
              {rule.met ? '✓' : '✗'}
            </span>
            <span>{rule.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SignalCard ─────────────────────────────────────────────────────────────────
// Extracted sub-component so useInsightExplanation can be called at the
// component's top level (React Rules of Hooks: hooks cannot run in .map()).

interface SignalCardProps {
  insightType: InsightType;
  flag: { active: boolean; age: number };
  customer: Customer;
  openSignal: InsightType | null;
  onToggle: () => void;
}

/**
 * A single active signal entry in the customer drawer Signals section.
 * Fetches an AI-generated explanation (SCRUM-49) and displays it below the
 * existing static signal reason when the info button is clicked.
 * Falls back silently when the gateway is unavailable.
 */
function SignalCard({ insightType, flag, customer, openSignal, onToggle }: SignalCardProps) {
  const meta = INSIGHT_TYPES[insightType];
  const freshness = freshnessFromAge(flag.age);
  const color = `var(${meta.colorVar})`;
  const open = openSignal === insightType;

  // Derive non-PII facts from the flag and customer tier.
  // F-2 audit fix: access flag.age directly (flag is already destructured above),
  // NOT insight.flag.age.
  const explanationReq: ExplanationRequest = {
    insightType,
    facts: [
      { label: 'Severity', value: flag.age > 60 ? 'HIGH' : 'MED' },
      { label: 'Age', value: `${flag.age.toString()} days` },
      { label: 'Account tier', value: customer.tier },
    ],
  };

  const { data: explanation, isLoading: explanationLoading } =
    useInsightExplanation(explanationReq);

  return (
    <div className="border-border-0 flex items-start gap-2.5 border-b py-2 last:border-0">
      <span
        className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <div className="min-w-0 flex-1">
        <div className="text-text-0 flex items-center gap-1.5 text-sm font-medium">
          {meta.label}
          <button
            type="button"
            title="Why did this trigger?"
            onClick={onToggle}
            className={`inline-flex h-4 w-4 items-center justify-center rounded-full ${
              open ? 'text-brand-primary' : 'text-text-3 hover:text-text-1'
            }`}
          >
            <Info className="h-3 w-3" />
          </button>
        </div>

        <div className="text-text-3 mt-1 flex items-center gap-2 text-xs">
          <span>Triggered {flag.age}d ago</span>
          <span className="flex flex-1 items-center gap-1.5">
            <span className="bg-bg-3 h-1 flex-1 overflow-hidden rounded-full">
              <span
                className="block h-full rounded-full"
                style={{ width: `${String(freshness)}%`, backgroundColor: color }}
              />
            </span>
            {/* SCRUM-843: labelled "fresh" because the number is signal freshness,
                not detection confidence — an unlabelled % next to a signal reads as
                confidence, which is how a $0-balance customer appeared 81% confident. */}
            <span style={{ color }}>{freshness}% fresh</span>
          </span>
        </div>

        {/* Expanded detail: static reason + AI explanation (SCRUM-49) */}
        {open && (
          <div className="mt-2 space-y-1.5">
            {/* Static deterministic reason — always shown when open */}
            <p className="text-text-1 bg-bg-3 rounded-md px-2 py-1.5 text-xs leading-relaxed">
              {signalReason(customer, insightType)}
            </p>

            {/* AI explanation — loading skeleton → paragraph + badge → nothing on unavailability */}
            {explanationLoading ? (
              <div
                className="bg-bg-3 h-4 w-3/4 animate-pulse rounded"
                aria-label="Loading explanation"
              />
            ) : explanation?.aiGenerated === true && explanation.content ? (
              <div>
                <p className="text-text-2 text-xs leading-relaxed">{explanation.content}</p>
                <span className="text-text-3 mt-0.5 flex items-center gap-1 text-[0.625rem]">
                  <Sparkles className="h-2.5 w-2.5" aria-hidden="true" />
                  AI-generated
                </span>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
